import { call, take, takeEvery, put } from 'redux-saga/effects';

import { getQcsSyncResources, qcsSyncCreateResource, qcsSyncUpdateResource, qcsSyncDeleteResource }
  from 'data/qcsSyncResourceSagaHelpers';
import { mkQcsSyncResourceConfig } from 'data/qcsSyncResourceTypes';
import isAcme from 'isAcme';

import * as actions from './actions';
import { getLastSyncDate } from './selectors';
import { transformResponseToMemorizedRules, transformMemorizedRuleToRequestData } from './transformers';
import { mkMemorizedRule } from './types';

// ===============================================================================================
// SAGAS functions for memorizedRules.  These are the functions responsible for communicating directly
// with external endpoints asynchronously.

// ====================================================
// ACTION WATCHERS to trigger SAGAS calls

const mkResourceConfig = (successAction, failureAction) => mkQcsSyncResourceConfig({
  resourceName: 'memorizedRule',
  resourceBaseUrl: '/memorized-rules',
  getLastSyncDate,

  transformResponseToResources: transformResponseToMemorizedRules,
  transformResourceToRequestData: transformMemorizedRuleToRequestData,

  successAction,
  failureAction,
});

export function* makeMemorizedRuleFromTransaction(action) {

  const txn = action.payload;
  const quickenType = txn.amount > 0 ? 'DEPOSIT' : 'PAYMENT';
  const newRule = mkMemorizedRule({
    isDeleted: false,
    payee: txn.payee,
    transactionType: isAcme ? null : quickenType,
    memo: txn.memo,
    amount: txn.amount,
    isNotAutoCategorized: false,
    isNotEditableByTransactions: false,
    isExcludedFromCalendar: true,
    coa: txn.coa,
    tags: txn.tags,
    split: txn.split,
  });
  yield put(actions.createMemorizedRule(newRule));
}

export function* getMemorizedRulesActionWatcher() {
  // using a vanilla saga pattern so we only process a single get request
  //   - takeEvery would process every request
  //   - takeLatest would cancel any current request and start request again
  while (true) {
    const action = yield take(actions.getMemorizedRules);

    const config = mkResourceConfig(actions.getMemorizedRulesSuccess, actions.getMemorizedRulesFailure);
    yield call(getQcsSyncResources, config, action);
  }
}

export function* createMemorizedRuleActionWatcher() {
  const config = mkResourceConfig(actions.createMemorizedRuleSuccess, actions.createMemorizedRuleFailure);
  yield takeEvery(actions.createMemorizedRule, qcsSyncCreateResource, config);
}

export function* makeMemorizedRuleFromTransactionActionWatcher() {
  yield takeEvery(actions.makeMemorizedRuleFromTransactionAction, makeMemorizedRuleFromTransaction);
}

export function* updateMemorizedRuleActionWatcher() {
  const config = mkResourceConfig(actions.updateMemorizedRuleSuccess, actions.updateMemorizedRuleFailure);
  yield takeEvery(actions.updateMemorizedRule, qcsSyncUpdateResource, config);
}

export function* deleteMemorizedRuleActionWatcher() {
  const config = mkResourceConfig(actions.deleteMemorizedRuleSuccess, actions.deleteMemorizedRuleFailure);
  yield takeEvery(actions.deleteMemorizedRule, qcsSyncDeleteResource, config);
}


// ====================================================
// EXPORTS

export default [
  getMemorizedRulesActionWatcher,
  createMemorizedRuleActionWatcher,
  updateMemorizedRuleActionWatcher,
  deleteMemorizedRuleActionWatcher,
  makeMemorizedRuleFromTransactionActionWatcher,
];
